// Lógica pura da feature, sem imports: a view, a store e os testes usam daqui.

export const DIVERGENCE_STATUSES = ['AWAITING_APPROVAL', 'APPROVED', 'REFUSED', 'CONTESTED', 'REVERSED']

export const DIVERGENCE_TYPES = [
  'AREA',
  'FURNITURE',
  'TROUSSEAU',
  'DISPLACEMENT',
  'TRIP_REIMBURSEMENT',
]

/**
 * As duas naturezas de pedido. O valor vira o parâmetro `reimbursement` da listagem: nulo traz as
 * duas, e é por isso que o filtro guarda string e não booleano — o "Todos" precisa não ir na query.
 */
export const DIVERGENCE_KINDS = ['RECALCULATION', 'REIMBURSEMENT']

export function isReimbursement(item) {
  return Boolean(item && item.reimbursement)
}

/** Precedência do status resolvido quando o lote foi misto. */
const RESOLVED_STATUS_PRECEDENCE = ['REVERSED', 'CONTESTED', 'APPROVED', 'REFUSED']

export function buildOptions(values, i18n, prefix) {
  return values.map((value) => ({ name: i18n(`${prefix}.${value}`), value }))
}

/**
 * Status mostrado na linha. Pendente vence enquanto houver item aguardando análise; resolvido,
 * mostra o que aconteceu, dando prioridade ao recálculo sobre a negativa.
 */
export function rowStatus(item) {
  if (!item) return 'AWAITING_APPROVAL'
  if (item.has_pending_items) return 'AWAITING_APPROVAL'
  if (isAwaitingPayment(item)) return 'AWAITING_PAYMENT'
  if (isPaidReimbursement(item)) return 'PAID'
  const resolved = RESOLVED_STATUS_PRECEDENCE.find((status) => item.statuses?.includes(status))
  return resolved || 'AWAITING_APPROVAL'
}

/**
 * Approval already settled the reimbursement: it paid the host, or the contract passes no
 * displacement on and the host's share was zero — then there is no payment, but the applied amount
 * is recorded. Only approvals from before the approval started paying lack both.
 */
function isSettledReimbursementItem(current) {
  return Boolean(current.payment_id) || current.applied_host_delta != null
}

/**
 * A reimbursement approved before the approval started paying: the payment was entered by hand
 * and, until then, nobody received anything. Approvals now create the payment right away.
 */
export function isAwaitingPayment(item) {
  if (!item || !item.reimbursement) return false
  const approved = (item.items || []).filter((current) => current.status === 'APPROVED')
  return approved.length > 0 && approved.every((current) => !isSettledReimbursementItem(current))
}

/**
 * An approved reimbursement already settled on the host's wallet. It is never "Recalculated":
 * nothing was recalculated, the trip was paid.
 */
export function isPaidReimbursement(item) {
  if (!item || !item.reimbursement) return false
  const approved = (item.items || []).filter((current) => current.status === 'APPROVED')
  return approved.length > 0 && approved.every(isSettledReimbursementItem)
}

/** Status shown on a single item: a reimbursement is paid or awaiting payment, never recalculated. */
export function itemStatus(request, item) {
  if (!item) return 'AWAITING_APPROVAL'
  if (isReimbursement(request) && item.status === 'APPROVED') {
    return isSettledReimbursementItem(item) ? 'PAID' : 'AWAITING_PAYMENT'
  }
  return item.status
}

/**
 * O valor do ressarcimento na listagem: o aprovado pela análise quando já houve decisão, senão o
 * que o host declarou. Não sai de `estimated_host_delta_sum`, que só soma itens ainda em aberto.
 * Nulo quando ainda não há valor: o app atual não manda valor, quem digita é a análise.
 */
export function reimbursementAmount(item) {
  const items = (item && item.items) || []
  const approved = items.find((current) => current.approved_reimbursement_value != null)
  if (approved) return approved.approved_reimbursement_value
  const declared = items.find((current) => current.declared_displacement_value != null)
  return declared ? declared.declared_displacement_value : null
}

/**
 * A taxa do host digitada no campo: número ou nula quando vazia. O campo `type="number"` devolve
 * string, e vazio não pode virar 0 — 0 é uma decisão (host não recebe nada), vazio não é.
 */
export function parseHostPercent(value) {
  if (value == null || String(value).trim() === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

/** A taxa do host é uma porcentagem do valor: de 0 a 100, com 0 valendo (host não recebe nada). */
export function isValidHostPercent(percent) {
  return percent != null && percent >= 0 && percent <= 100
}

/**
 * Quanto o host recebe de um ressarcimento cobrado da imobiliária: a taxa do host, que abre com a
 * taxa de deslocamento do contrato (`reimbursement_host_percent`) e pode ser trocada na aprovação.
 * Lida como no contrato e no adicional — taxa zero, host recebe zero.
 * Conta em centavos inteiros e arredonda como o backend (HALF_EVEN), para a prévia bater com o
 * lançamento. Nulo sem a taxa: a tela não inventa um número que a aprovação não vai usar.
 */
export function reimbursementHostValue(value, percent) {
  if (percent == null) return null
  const valueCents = Math.round(Number(value) * 100)
  const percentHundredths = Math.round(Number(percent) * 100)
  const scaled = valueCents * percentHundredths
  const cents = Math.floor(scaled / 10000)
  const remainder = scaled - cents * 10000
  const rounded = remainder > 5000 || (remainder === 5000 && cents % 2 !== 0) ? cents + 1 : cents
  return rounded / 100
}

export function statusColor(status) {
  const colors = {
    AWAITING_APPROVAL: 'orange lighten-4',
    AWAITING_PAYMENT: 'amber lighten-4',
    PAID: 'green lighten-4',
    APPROVED: 'green lighten-4',
    REFUSED: 'red lighten-4',
    CONTESTED: 'deep-orange lighten-4',
    REVERSED: 'blue-grey lighten-4',
  }
  return colors[status] || 'grey lighten-2'
}

/**
 * Ação da modal para o ressarcimento. Ele só aprova ou nega: não há recálculo a simular, não há
 * cadastro a corrigir e a imobiliária não é debitada por ele, então não há o que contestar.
 */
export function resolveReimbursementAction(request) {
  return itemsWithStatus(request, 'AWAITING_APPROVAL').length > 0 ? 'reviewReimbursement' : null
}

/**
 * Valor sugerido do ressarcimento, que sai da taxa de deslocamento do contrato no momento do
 * pedido. Não passa pelo simulate: num agendamento cancelado não há imóvel faturável e o cálculo
 * do serviço daria zero.
 */
export function reimbursementDelta(request) {
  const item = actionableItems(request).find((current) => current.estimated_host_delta != null)
  if (!item) return null
  return {
    host_delta: item.estimated_host_delta,
    real_estate_delta: item.estimated_real_estate_delta,
  }
}

/** Só itens pendentes entram no recálculo: os resolvidos ficam apenas no histórico da linha. */
export function pendingItems(request) {
  if (!request || !request.items) return []
  return request.items.filter((item) => item.status === 'AWAITING_APPROVAL')
}

export function pendingItemIds(request) {
  return pendingItems(request).map((item) => item.id)
}

/**
 * O backend pagina a partir de zero; a store do front conta a partir de um.
 *
 * O filtro de natureza viaja como `kind` no estado e vira `reimbursement` na query. A tradução
 * acontece aqui porque o backend espera booleano, e o filtro precisa de um terceiro estado — o
 * "Todos", que não pode ir na query de jeito nenhum, senão esconde metade da fila.
 */
export function buildPaginationParams({ itemsPerPage, currentPage, filters }) {
  const { kind, ...rest } = filters || {}
  return {
    size: itemsPerPage,
    page: Math.max(0, (currentPage || 1) - 1),
    ...rest,
    ...(kind ? { reimbursement: kind === 'REIMBURSEMENT' } : {}),
  }
}

/**
 * Achata as evidências de todos os itens em pares {divergenceId, fileId}, que é o que o endpoint
 * de URL assinada precisa. Item sem foto simplesmente não entra. Não filtra por status: a modal
 * mostra a foto de todo item, e pedido só leitura (negado, estornado) também precisa dela.
 */
export function collectEvidencePairs(request) {
  if (!request || !request.items) return []
  return request.items.flatMap((item) =>
    (item.file_ids || []).map((fileId) => ({ divergenceId: item.id, fileId }))
  )
}

/**
 * Modo da modal, derivado do que o pedido tem de acionável. A ordem importa: item pendente sempre
 * vem primeiro, porque analisar precede contestar.
 */
const ACTION_BY_STATUS = [
  { status: 'AWAITING_APPROVAL', action: 'review' },
  { status: 'APPROVED', action: 'contest' },
  { status: 'CONTESTED', action: 'reverse' },
]

export function itemsWithStatus(request, status) {
  if (!request || !request.items) return []
  return request.items.filter((item) => item.status === status)
}

/** 'review' | 'contest' | 'reverse' | null — nulo quando o pedido é só leitura. */
export function resolveModalAction(request) {
  const match = ACTION_BY_STATUS.find(({ status }) => itemsWithStatus(request, status).length > 0)
  return match ? match.action : null
}

/** Itens sobre os quais a ação corrente age. Nunca mistura status. */
export function actionableItems(request) {
  const match = ACTION_BY_STATUS.find(({ status }) => itemsWithStatus(request, status).length > 0)
  return match ? itemsWithStatus(request, match.status) : []
}

export function actionableItemIds(request) {
  return actionableItems(request).map((item) => item.id)
}

/**
 * Itens que o analista marca ou desmarca. Só a análise decide item a item: contestação e estorno
 * agem no lote inteiro, e o backend recusa estorno parcial.
 */
export function isSelectable(request, item) {
  // o ressarcimento é um item só, decidido inteiro: seleção ali não significa nada
  if (isReimbursement(request)) return false
  return resolveModalAction(request) === 'review' && item.status === 'AWAITING_APPROVAL'
}

/**
 * Distância a partir da qual o checkin merece atenção: até aí é outra quadra, acima disso é outro
 * lugar. Só pinta o número — não bloqueia nada, quem decide é o analista.
 */
export const CHECK_IN_FAR_METERS = 1000

/**
 * Resposta do checklist (`admin/job/checkIn/{id}`), em camelCase: `date`, `latitude`, `longitude`.
 * Coordenada só existe no fluxo de checklist — agendamento finalizado pelo app antigo tem data e
 * nenhuma localização, e a tela precisa dizer isso em vez de mostrar um mapa vazio.
 */
export function hasCheckInLocation(checkIn) {
  return !!checkIn && checkIn.latitude != null && checkIn.longitude != null
}

/** Link do mapa no ponto do checkin. Nulo quando não há coordenada. */
export function checkInMapUrl(checkIn) {
  if (!hasCheckInLocation(checkIn)) return null
  return `https://www.google.com/maps/search/?api=1&query=${checkIn.latitude},${checkIn.longitude}`
}

const EARTH_RADIUS_METERS = 6371000

/** Haversine, em metros. A conta é aqui porque os dois pontos vêm de APIs diferentes. */
export function distanceInMeters(fromLatitude, fromLongitude, toLatitude, toLongitude) {
  const toRadians = (degrees) => (Number(degrees) * Math.PI) / 180
  const fromLatitudeRadians = toRadians(fromLatitude)
  const toLatitudeRadians = toRadians(toLatitude)
  const latitudeDelta = toLatitudeRadians - fromLatitudeRadians
  const longitudeDelta = toRadians(Number(toLongitude) - Number(fromLongitude))

  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(fromLatitudeRadians) * Math.cos(toLatitudeRadians) * Math.sin(longitudeDelta / 2) ** 2

  return Math.round(EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine)))
}

/**
 * Distância do checkin até o imóvel da divergência. O deslocamento não tem imóvel próprio, então
 * cai para a coordenada da linha, que é a do primeiro imóvel do agendamento. Nula quando falta
 * qualquer um dos dois pontos.
 */
export function checkInDistance(checkIn, item, request) {
  if (!hasCheckInLocation(checkIn) || !item) return null
  const latitude = item.realty_latitude != null ? item.realty_latitude : request && request.realty_latitude
  const longitude = item.realty_longitude != null ? item.realty_longitude : request && request.realty_longitude
  if (latitude == null || longitude == null) return null
  return distanceInMeters(checkIn.latitude, checkIn.longitude, latitude, longitude)
}

export function isCheckInFar(meters) {
  return meters != null && meters >= CHECK_IN_FAR_METERS
}

/** Metros abaixo de 1 km, quilômetros com uma casa acima disso. Sem distância, sem texto. */
const ENGLISH_LANGUAGE = /^(en|au)/i
const METERS_IN_KILOMETER = 1000

export function formatDistance(meters, appLanguage) {
  if (meters == null) return null
  if (meters < METERS_IN_KILOMETER) return `${Math.round(meters)} m`
  const locale = ENGLISH_LANGUAGE.test(appLanguage || '') ? 'en-US' : 'pt-BR'
  const kilometers = (meters / METERS_IN_KILOMETER).toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
  return `${kilometers} km`
}

/** Valor já lançado, para mostrar em pedido resolvido em vez de simular de novo. */
export function appliedDelta(request) {
  const item = actionableItems(request).find((current) => current.applied_host_delta != null)
  if (!item) return null
  return {
    host_delta: item.applied_host_delta,
    real_estate_delta: item.applied_real_estate_delta,
  }
}

/**
 * Starting value of the displacement field: the host's suggestion, as the text the money mask
 * expects. The mask reads digits as cents, so a bare `300` would show up as R$ 3,00.
 */
export function initialDisplacementValue(request) {
  const displacement = ((request && request.items) || []).find((item) => item.type === 'DISPLACEMENT')
  const suggested = Number(displacement?.declared_displacement_value)
  return suggested > 0 ? suggested.toFixed(2) : 0
}
